"use client";

import { useCallback, useRef, useState, type MutableRefObject, type RefObject } from "react";
import { Pause, Play } from "lucide-react";
import { Slider } from "@speakai/ui/slider";
import { useTranslation } from "react-i18next";
import { formatClock } from "@speakai/common/src/utils/clock";

const SEEK_STEP_SECONDS = 0.1;

interface AudioPreviewPlayerProps {
  src: string;
  /** Take length in seconds, from the recording timer. */
  duration: number;
  /** Receives the <audio> element for setSinkId speaker routing. */
  mediaRef?: RefObject<HTMLAudioElement | HTMLVideoElement | null>;
}

export function AudioPreviewPlayer({ src, duration, mediaRef }: AudioPreviewPlayerProps) {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  const setAudioRef = useCallback(
    (el: HTMLAudioElement | null) => {
      audioRef.current = el;
      if (mediaRef) (mediaRef as MutableRefObject<HTMLAudioElement | HTMLVideoElement | null>).current = el;
    },
    [mediaRef],
  );

  const togglePlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play().catch(() => setIsPlaying(false));
    } else {
      audio.pause();
    }
  };

  const playLabel = isPlaying ? t("ACTION.PAUSE", "Pause") : t("RECORD.PLAY", "Play");
  const PlaybackIcon = isPlaying ? Pause : Play;

  return (
    <div
      className="flex items-center gap-3 rounded-xl border border-border bg-muted/40 p-3"
      data-testid="recorder-audio-player"
    >
      <button
        type="button"
        onClick={togglePlayback}
        aria-label={playLabel}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform active:scale-[0.92] motion-reduce:transition-none motion-reduce:active:scale-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        data-testid="recorder-audio-play"
      >
        <PlaybackIcon className="h-5 w-5" fill="currentColor" aria-hidden="true" />
      </button>
      <Slider
        inline
        value={Math.min(currentTime, duration)}
        min={0}
        max={Math.max(duration, SEEK_STEP_SECONDS)}
        step={SEEK_STEP_SECONDS}
        label={t("RECORD.PLAYBACK_POSITION", "Playback position")}
        onChange={(seconds) => {
          if (audioRef.current) audioRef.current.currentTime = seconds;
          setCurrentTime(seconds);
        }}
        className="min-w-0 flex-1 [&>*]:w-full"
      />
      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
        {formatClock(currentTime, { padMinutes: false })} / {formatClock(duration, { padMinutes: false })}
      </span>
      <audio
        ref={setAudioRef}
        src={src}
        preload="metadata"
        className="hidden"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        data-testid="recorder-audio-preview"
      />
    </div>
  );
}
